import { Box, Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import * as yup from "yup";
import { driverLicenceSchema, passportSchema, regoSchema, validDriverLicenceSchema, validPassportSchema } from "../../api";
import { Client } from "../../common/types";
import { SectionTitleText, SubtitleText } from "../controls/helpers";
import { DateTextInput, ListSelect, NumberTextInput, TextInput } from "../controls/inputs";

const requiredPassportSchema = passportSchema
  .concat(
    yup
      .object({
        passportNumber: yup.string().required("Passport number is required"),
        expiryDate: yup.date().required("Expiry date is required"),
      })
      .concat(validPassportSchema)
  )
  .default(undefined)
  .defined("NZ Passport or NZ Driver Licence is required");

const requiredDriverLicenceSchema = driverLicenceSchema.concat(
  yup
    .object({
      licenceNumber: yup.string().required("Licence number is required"),
      versionNumber: yup.string().required("Version number is required"),
    })
    .concat(validDriverLicenceSchema)
    .default(undefined)
);

export const identificationOptions = (inBrowserApp?: boolean) => ({
  passportAndDriverLicence: "NZ Passport & NZ Driver Licence (recommended)",
  passport: "NZ Passport only",
  driverLicence: "NZ Driver Licence only",
  none: `${inBrowserApp ? "I do" : "The client does"} not have a NZ Passport or NZ Driver Licence`,
});

export const identificationSchema = (skipIdentification?: boolean, inBrowserApp?: boolean) => {
  const { driverLicence, passport, passportAndDriverLicence } = identificationOptions(inBrowserApp);
  return yup.object({
    rego: regoSchema
      .concat(
        yup.object({
          numberPlate: yup.string().matches(/^[a-zA-Z0-9]+$/, "Please enter a valid plate number"),
        })
      )
      .default(undefined),
    passport: yup
      .object()
      .when("identificationOption", { is: passport, then: requiredPassportSchema })
      .when("identificationOption", { is: passportAndDriverLicence, then: requiredPassportSchema }),
    driverLicence: yup
      .object()
      .when("identificationOption", { is: driverLicence, then: requiredDriverLicenceSchema })
      .when("identificationOption", { is: passportAndDriverLicence, then: requiredDriverLicenceSchema }),
    identificationOption: !inBrowserApp || skipIdentification ? yup.string() : yup.string().required("An option is required"),
  });
};

export const getIdentificationOption = (skipIdentification?: boolean, inBrowserApp?: boolean, values?: Client) => {
  const { none, passportAndDriverLicence, passport, driverLicence } = identificationOptions(inBrowserApp);
  if (skipIdentification) return none;
  else if (!!values?.passport && !!values?.driverLicence) return passportAndDriverLicence;
  else if (!!values?.passport && !values?.driverLicence) return passport;
  else if (!values?.passport && !!values?.driverLicence) return driverLicence;
  else return "";
};

const SelectIdentification = ({ inBrowserApp }: { inBrowserApp: boolean | undefined }) => {
  const { passportAndDriverLicence, passport, driverLicence, none } = identificationOptions(inBrowserApp);
  const { setFieldValue, values } = useFormikContext<Client>();

  return (
    <Box mb={3}>
      <SectionTitleText>Identification</SectionTitleText>
      <SubtitleText>Providing both Passport &amp; Driver Licence details will increase the likelihood of successful verification.</SubtitleText>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ListSelect
            hideLabel
            name="identificationOption"
            value={values.identificationOption || ""}
            options={Object.values(identificationOptions(inBrowserApp))}
            onChange={(_, value) => {
              setFieldValue("identificationOption", value);
            }}
          />
        </Grid>
        {(values.identificationOption === passport || values.identificationOption === passportAndDriverLicence) && (
          <>
            <Grid item sm={6} xs={12}>
              <TextInput label="NZ Passport number" name="passport.passportNumber" />
            </Grid>
            <Grid item sm={6} xs={12}>
              <DateTextInput label="NZ Passport expiry date" name="passport.expiryDate" />
            </Grid>
          </>
        )}
        {(values.identificationOption === driverLicence || values.identificationOption === passportAndDriverLicence) && (
          <>
            <Grid item sm={6} xs={12}>
              <TextInput label="NZ Driver Licence number" name="driverLicence.licenceNumber" />
            </Grid>
            <Grid item sm={6} xs={12}>
              <NumberTextInput label="NZ Driver Licence version number" name="driverLicence.versionNumber" mask="999" setStringValue />
            </Grid>
          </>
        )}
        {!!values.identificationOption && values.identificationOption !== none && (
          <>
            <Grid item xs={12}>
              <SubtitleText hideMarginBottom>If {inBrowserApp ? "you own" : "the client owns"} a vehicle, enter the number plate (optional).</SubtitleText>
            </Grid>
            <Grid item sm={6} xs={12}>
              <TextInput maxLength="6" label="Vehicle number plate" name="rego.numberPlate" />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default SelectIdentification;
